/**
 * Home Page / Landing Page
 * Phase 6: Frontend Application
 *
 * Shows a professional login screen for unauthenticated users.
 * Redirects authenticated users to the Dashboard.
 * 
 * Updated: 2026-01-04 - Trigger frontend deployment after dependency fix
 */

'use client';

import { isAuthConfigured, useAuth } from '@/lib/auth';
import {
    BookOpen,
    ChefHat,
    Lock,
    Sparkles,
    Utensils,
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import React, { useEffect } from 'react';

/**
 * Feature card for the landing page
 */
function FeatureCard({
  icon: Icon,
  title,
  description,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}): JSX.Element {
  return (
    <div className="flex flex-col items-center text-center p-6">
      <div className="mb-4 rounded-full bg-primary-100 p-3">
        <Icon className="h-6 w-6 text-primary-600" />
      </div>
      <h3 className="mb-2 font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
  );
}

/**
 * Login screen for unauthenticated users
 */
function LoginScreen(): JSX.Element {
  const { login, isLoading } = useAuth();

  const handleLogin = async (): Promise<void> => {
    try {
      await login();
    } catch (error) {
      // Login errors are handled by the auth provider
      // Error details available in browser devtools network tab
    }
  };

  // Check if auth is configured
  const authReady = isAuthConfigured();

  return (
    <div className="min-h-[calc(100vh-8rem)] flex flex-col">
      {/* Hero Section */}
      <div className="flex-1 flex items-center justify-center bg-gradient-to-b from-primary-50 to-white px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <div className="rounded-full bg-primary-600 p-4 shadow-lg">
              <ChefHat className="h-16 w-16 text-white" />
            </div>
          </div>

          {/* Title */}
          <h1 className="mb-4 font-display text-4xl font-bold text-gray-900 sm:text-5xl lg:text-6xl">
            Recipe Manager
          </h1>
          <p className="mb-8 text-lg text-gray-600 sm:text-xl max-w-2xl mx-auto">
            Enterprise recipe management platform with AI-powered enrichment 
            and UK nutrition analysis
          </p>

          {/* Login Card */}
          <div className="mx-auto max-w-md">
            <div className="rounded-2xl bg-white p-8 shadow-xl border border-gray-100">
              <div className="mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Lock className="h-5 w-5 text-primary-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Secure Enterprise Login
                  </span>
                </div>
                <p className="text-sm text-gray-500">
                  Sign in with your organisation&apos;s Microsoft account
                </p>
              </div>

              {authReady ? (
                <button
                  onClick={handleLogin}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-3 rounded-lg bg-[#2F2F2F] px-6 py-4 text-white font-medium hover:bg-[#1F1F1F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 21 21"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect width="10" height="10" fill="#F25022" />
                        <rect x="11" width="10" height="10" fill="#7FBA00" />
                        <rect y="11" width="10" height="10" fill="#00A4EF" />
                        <rect x="11" y="11" width="10" height="10" fill="#FFB900" />
                      </svg>
                      <span>Sign in with Microsoft</span>
                    </>
                  )}
                </button>
              ) : (
                <div className="text-center py-4">
                  <Lock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">
                    Authentication is not configured.
                    <br />
                    Please contact your administrator.
                  </p>
                </div>
              )}

              <p className="mt-4 text-xs text-gray-400 text-center">
                Protected by Microsoft Entra ID
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white border-t border-gray-100 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-center text-2xl font-bold text-gray-900 mb-12">
            Streamline Your Recipe Management
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={Utensils}
              title="Recipe Management"
              description="Create, edit, and organise recipes with a structured workflow from draft to publication."
            />
            <FeatureCard
              icon={Sparkles}
              title="AI Enrichment"
              description="Automatically normalise ingredients and enhance recipes with intelligent suggestions."
            />
            <FeatureCard
              icon={BookOpen}
              title="UK Nutrition"
              description="Calculate accurate UK nutrition values per serving and per 100g with traffic light labels."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Home page - shows login screen or redirects to dashboard
 * 
 * NOTE: For static export with SWA, this page serves as the fallback entry point
 * for ALL routes. When a deep link like /recipes/123 is accessed, SWA serves
 * index.html (this page). We detect this and perform a hard redirect to the
 * correct placeholder page which has the proper RSC data for that route.
 */
export default function HomePage(): JSX.Element {
  const { isAuthenticated, isLoading } = useAuth();
  const router = useRouter();
  const [isNavigating, setIsNavigating] = React.useState(false);

  // Handle deep links: redirect to the correct placeholder page
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentPath = window.location.pathname;
      
      // If we're on a deep link (not home page), redirect to correct placeholder
      if (currentPath !== '/' && currentPath !== '') {
        // Determine which placeholder page to use based on the route
        let targetUrl = currentPath;
        
        // For dynamic routes, redirect to the static view/edit page with query param
        // This avoids SWA/Next.js static export issues with dynamic routes by using query params
        const recipeMatch = currentPath.match(/^\/recipes\/([^/]+)$/);
        const recipeEditMatch = currentPath.match(/^\/recipes\/([^/]+)\/edit$/);

        if (recipeMatch) {
          // Use query param approach (Lesson L012 update)
          targetUrl = `/recipes/view?id=${recipeMatch[1]}`;
        } else if (recipeEditMatch) {
          targetUrl = `/recipes/view/edit?id=${recipeEditMatch[1]}`;
        } else {
          const menuMatch = currentPath.match(/^\/menus\/([^/]+)$/);
          const menuShoppingMatch = currentPath.match(/^\/menus\/([^/]+)\/shopping-list$/);
          const guideMatch = currentPath.match(/^\/guides\/([^/]+)$/);
          const guideEditMatch = currentPath.match(/^\/guides\/([^/]+)\/edit$/);

          if (menuMatch) {
            targetUrl = `/menus/view?id=${menuMatch[1]}`;
          } else if (menuShoppingMatch) {
            targetUrl = `/menus/view/shopping-list?id=${menuShoppingMatch[1]}`;
          } else if (guideMatch) {
            targetUrl = `/guides/view?id=${guideMatch[1]}`;
          } else if (guideEditMatch) {
            targetUrl = `/guides/view/edit?id=${guideEditMatch[1]}`;
          }
        }
        
        // Only redirect if we found a matching placeholder
        // Store the original URL so the placeholder page can extract the ID
        if (targetUrl !== currentPath) {
          setIsNavigating(true);
          // Store original path in sessionStorage for the placeholder to read
          sessionStorage.setItem('deepLinkPath', currentPath);
          window.location.href = targetUrl;
          return;
        }
      }
      
      // If actually on home page and authenticated, redirect to dashboard
      if (isAuthenticated && !isLoading) {
        router.replace('/dashboard');
      }
    }
  }, [isAuthenticated, isLoading, router]);

  // Show loading while navigating to deep link
  if (isNavigating) {
    return (
      <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary-600 border-t-transparent mx-auto" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show loading state for actual home page
  if (isLoading || isAuthenticated) {
    return (
      <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary-600 border-t-transparent mx-auto" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login screen for unauthenticated users
  return <LoginScreen />;
}
